import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarClock, ChevronLeft, ChevronRight, Tag, Calendar, Table as TableIcon, CalendarDays, Plus, X, Loader2, Save, AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { api } from '../lib/api.js'
import { smoothPath } from '../lib/chart.js'
import { fmtDateOnly, fmtRange, cmpDateTime } from '../lib/dt.js'
import {
  CARD, CARD_BASE, FilterButton, ChipPanel, DatePanel, ActiveChip, ViewSwitcher,
  CompanyCombobox, Field, PeriodPills,
} from '../components/ui.jsx'
import { CompanyLogoAvatar } from './Companies.jsx'
import { thb } from '../lib/billing.js'
import { IncomeDashboard } from './_IncomeDashboard.jsx'
import { windowFor, addDays, PERIODS } from '../lib/periods.js'

const STAGES = [
  'draft', 'ระหว่างดำเนินการ', 'confirmed', 'complete', 'cancelled',
]
const STAGE_DOT = {
  draft:              'bg-zinc-400',
  'ระหว่างดำเนินการ': 'bg-amber-500',
  confirmed:          'bg-blue-500',
  complete:           'bg-emerald-500',
  cancelled:          'bg-rose-500',
}
const STAGE_BADGE = {
  draft:              'bg-zinc-50 ring-zinc-200/70 text-zinc-700',
  'ระหว่างดำเนินการ': 'bg-amber-50 ring-amber-200/80 text-amber-900',
  confirmed:          'bg-blue-50 ring-blue-200/80 text-blue-800',
  complete:           'bg-emerald-50 ring-emerald-200/80 text-emerald-800',
  cancelled:          'bg-rose-50 ring-rose-200/80 text-rose-800',
}

const VIEWS = [
  { v: 'table',    label: 'Table',    icon: TableIcon    },
  { v: 'calendar', label: 'Calendar', icon: CalendarDays },
]

export default function Operations() {
  const [rows, setRows]       = useState(null)
  const [error, setError]     = useState(null)
  const [viewMode, setViewMode] = useState('table')
  const [creating, setCreating] = useState(false)
  const [refreshNonce, setRefreshNonce] = useState(0)

  // Filters — shared across both views
  const [stage, setStage]       = useState([])
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo]     = useState('')

  // Shared period filter for the three summary charts (activity hero, op-days
  // trend, treatment income). One window drives all three; the windowed
  // operations are fetched once here and passed down. Op-days are computed
  // client-side from these rows; income sums per-op revenue (in IncomeDashboard).
  const [statsPeriod, setStatsPeriod] = useState('6m')
  const statsWin = useMemo(() => windowFor(statsPeriod), [statsPeriod])
  const [statsOps, setStatsOps] = useState(null)   // null = loading · 'error' · row[]
  useEffect(() => {
    let cancelled = false
    setStatsOps(null)
    const from = statsWin.prevStart || statsWin.curStart
    api.operations.list({ stage: ['confirmed', 'complete'], date_from: from, date_to: statsWin.curEnd })
      .then((res) => { if (!cancelled) setStatsOps(Array.isArray(res) ? res : (res?.items || res?.data || [])) })
      .catch(() => { if (!cancelled) setStatsOps('error') })
    return () => { cancelled = true }
  }, [statsWin.curStart, statsWin.curEnd, statsWin.prevStart, refreshNonce])

  // Calendar mode — drives a separate date window derived from the month anchor.
  const [monthAnchor, setMonthAnchor] = useState(() => firstOfMonth(new Date()))

  // Effective window: in calendar mode we override the user's date filter with
  // the visible month's grid range so the cells always have what they need.
  const effFrom = viewMode === 'calendar' ? toDateStr(gridStart(monthAnchor)) : dateFrom
  const effTo   = viewMode === 'calendar' ? toDateStr(gridEnd(monthAnchor))   : dateTo

  useEffect(() => {
    setError(null)
    api.operations.list({
      stage,
      date_from: effFrom,
      date_to:   effTo,
    })
      .then(setRows)
      .catch((e) => setError(e.message))
  }, [stage.join(','), effFrom, effTo, viewMode, refreshNonce])


  const activeFilters = (stage.length ? 1 : 0) + ((dateFrom || dateTo) ? 1 : 0)
  const clearFilters = () => { setStage([]); setDateFrom(''); setDateTo('') }

  return (
    <div className="px-4 py-5 md:px-10 md:py-8 max-w-[1400px] mx-auto">
      <header className="mb-5 md:mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-3">
          <span aria-hidden className="hidden md:block mt-1.5 w-1 h-9 rounded-full bg-gradient-to-b from-amber-400 to-orange-500" />
          <div>
            <h1 className="text-[24px] md:text-[28px] font-bold tracking-tight text-zinc-900 leading-tight">Operations</h1>
            <p className="text-[13px] md:text-[13.5px] text-zinc-500 mt-1">Scheduled on-site service events.</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="text-[12.5px] text-zinc-500 tabular-nums inline-flex items-baseline gap-1.5">
            {rows
              ? <><span className="text-[15px] font-semibold text-zinc-900">{rows.length.toLocaleString()}</span><span>operations</span></>
              : 'Loading…'}
          </div>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold bg-amber-500 text-white hover:bg-amber-600 shadow-sm shadow-amber-500/25 transition-colors"
          >
            <Plus size={14} strokeWidth={2.5} />
            <span className="hidden sm:inline">New Operation</span>
            <span className="sm:hidden">New</span>
          </button>
          <ViewSwitcher value={viewMode} onChange={setViewMode} views={VIEWS} />
        </div>
      </header>

      {creating && (
        <NewOperationModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); setRefreshNonce((n) => n + 1) }}
        />
      )}

      <OperationsStats ops={statsOps} win={statsWin} period={statsPeriod} onPeriodChange={setStatsPeriod} />

      <IncomeDashboard ops={statsOps} win={statsWin} period={statsPeriod} onPeriodChange={setStatsPeriod} />

      {/* Toolbar — stage filter is always available; date filter shown only in table view */}
      <div className={`${CARD_BASE} relative z-10 mb-4`}>
        <div className="px-3 py-2.5 flex flex-col gap-2 md:flex-row md:items-center md:gap-2 md:flex-wrap">
          <FilterButton icon={Tag} label="Stage" count={stage.length}>
            <ChipPanel options={STAGES} value={stage} onChange={setStage} dotMap={STAGE_DOT} />
          </FilterButton>

          {viewMode === 'table' && (
            <FilterButton icon={Calendar} label="Date" count={(dateFrom || dateTo) ? 1 : 0}>
              <DatePanel direction="operations"
                from={dateFrom} to={dateTo}
                onChange={(f, t) => { setDateFrom(f); setDateTo(t) }} />
            </FilterButton>
          )}

          {viewMode === 'calendar' && (
            <MonthNav anchor={monthAnchor} onChange={setMonthAnchor} />
          )}

          {activeFilters > 0 && viewMode === 'table' && (
            <button onClick={clearFilters}
              className="px-2.5 py-1.5 rounded-md text-[12px] font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50">
              Clear
            </button>
          )}
        </div>

        {activeFilters > 0 && viewMode === 'table' && (
          <div className="px-3 pb-3 -mt-1 flex flex-wrap items-center gap-1.5">
            {stage.map((s) => (
              <ActiveChip key={`st-${s}`} icon={Tag} label="Stage" value={s}
                onRemove={() => setStage(stage.filter((x) => x !== s))} />
            ))}
            {(dateFrom || dateTo) && (
              <ActiveChip icon={Calendar} label="Date"
                value={`${dateFrom || '…'} → ${dateTo || '…'}`}
                onRemove={() => { setDateFrom(''); setDateTo('') }} />
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-[13px]">{error}</div>
      )}

      {viewMode === 'table'
        ? <OperationsTable rows={rows} />
        : <OperationsCalendar rows={rows} monthAnchor={monthAnchor} />}
    </div>
  )
}


/* ──────────────────────────────── Table view ──────────────────────────────── */

function OperationsTable({ rows }) {
  const navigate = useNavigate()
  const lastViewedId = useMemo(() => {
    const v = parseInt(sessionStorage.getItem('lastViewedOpId') || '', 10)
    return Number.isFinite(v) ? v : null
  }, [])
  const openOp = (id) => { sessionStorage.setItem('lastViewedOpId', String(id)); navigate(`/operations/${id}`) }

  const empty = rows && rows.length === 0

  return (
    <>
      {/* ── Mobile cards (< md) ── */}
      <div className="block md:hidden space-y-2">
        {!rows && (
          <div className={`${CARD} px-4 py-10 text-center text-zinc-400 text-[13px]`}>Loading…</div>
        )}
        {empty && (
          <div className={`${CARD} px-4 py-10 text-center`}>
            <CalendarClock size={20} className="mx-auto text-zinc-300" />
            <div className="mt-2 text-[13px] text-zinc-500">No operations match these filters.</div>
          </div>
        )}
        {rows && rows.map((o) => {
          const isLastViewed = o.id === lastViewedId
          return (
            <div
              key={o.id}
              onClick={() => openOp(o.id)}
              className={
                'rounded-2xl ring-1 shadow-[0_1px_2px_rgba(0,0,0,0.04)] px-3.5 py-3 flex items-start gap-3 cursor-pointer active:opacity-70 transition-opacity ' +
                (isLastViewed
                  ? 'bg-amber-50 ring-amber-300/50'
                  : 'bg-white ring-zinc-200/80')
              }
            >
              <CompanyLogoAvatar name={o.company_name} url={o.company_logo_url || null} size={38} />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-[13.5px] text-zinc-900 truncate leading-snug" title={o.company_name}>
                    {o.company_name}
                  </span>
                  <StageChip stage={o.stage} />
                </div>
                <div className="mt-0.5 text-[12px] text-zinc-500 tabular-nums">
                  {fmtRange(o.start_date, o.end_date, o.start_time, o.end_time)}
                  {o.expected_patients ? <> · N {o.expected_patients}</> : null}
                </div>
                <div className="mt-1">
                  <IncomeCell op={o} />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Desktop table (≥ md) ── */}
      <div className="hidden md:block">
        <div className={CARD}>
          <table className="w-full table-fixed text-[13px]">
            <colgroup>
              <col />
              <col className="w-[170px]" />
              <col className="w-[170px]" />
              <col className="w-[60px]" />
              <col className="w-[130px]" />
              <col className="w-[150px]" />
            </colgroup>
            <thead>
              <tr className="border-b border-zinc-100 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
                <th className="px-5 py-3 text-left">Company</th>
                <th className="px-5 py-3 text-left">Start</th>
                <th className="px-5 py-3 text-left">End</th>
                <th className="px-5 py-3 text-right">N</th>
                <th className="px-5 py-3 text-right">Income</th>
                <th className="px-5 py-3 text-left">Stage</th>
              </tr>
            </thead>
            <tbody>
              {!rows && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-zinc-400">Loading…</td></tr>
              )}
              {empty && (
                <tr><td colSpan={6} className="px-5 py-12 text-center">
                  <CalendarClock size={20} className="mx-auto text-zinc-300" />
                  <div className="mt-2 text-[13px] text-zinc-500">No operations match these filters.</div>
                </td></tr>
              )}
              {rows && rows.map((o) => {
                const isLastViewed = o.id === lastViewedId
                return (
                  <tr
                    key={o.id}
                    onClick={() => openOp(o.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOp(o.id) } }}
                    tabIndex={0}
                    role="link"
                    className={
                      'border-b border-zinc-100 last:border-b-0 transition-colors cursor-pointer focus:outline-none ' +
                      (isLastViewed
                        ? 'bg-amber-50 hover:bg-amber-100/70 focus:bg-amber-100/80 ring-1 ring-inset ring-amber-300/40'
                        : 'hover:bg-amber-50/40 focus:bg-amber-50/60')
                    }
                  >
                    <td className="px-5 py-2.5 align-top">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <CompanyLogoAvatar name={o.company_name} url={o.company_logo_url || null} size={32} />
                        <div className="font-medium text-zinc-900 truncate" title={o.company_name}>{o.company_name}</div>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-zinc-700 tabular-nums align-top">{fmtDateOnly(o.start_date)}{o.start_time ? ' · ' + o.start_time : ''}</td>
                    <td className="px-5 py-2.5 text-zinc-700 tabular-nums align-top">{fmtDateOnly(o.end_date)}{o.end_time ? ' · ' + o.end_time : ''}</td>
                    <td className="px-5 py-2.5 text-right text-zinc-700 tabular-nums align-top">{o.expected_patients || <span className="text-zinc-300">—</span>}</td>
                    <td className="px-5 py-2.5 text-right align-top"><IncomeCell op={o} /></td>
                    <td className="px-5 py-2.5 align-top"><StageChip stage={o.stage} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}


/** Per-operation income cell. Fetches THIS operation's revenue over its own
 *  date range — the same query the Treatment-tab income banner makes — so the
 *  two always agree. (The Xano chart/revenue report is range-sensitive — its
 *  per-day figures depend on the start_date passed — so a single shared wide
 *  query across all operations returned different numbers than the per-op view.
 *  /api/revenue caches each range 5 min, so repeat ranges stay cheap.) */
function IncomeCell({ op }) {
  const start = op.start_date
  const end   = op.end_date || op.start_date
  const [data, setData] = useState(null)   // null = loading · 'error' · { total, clinic }

  useEffect(() => {
    if (!start) { setData('error'); return }
    let cancelled = false
    setData(null)
    api.revenue.range(start, end)
      .then((d) => {
        if (cancelled) return
        let total = 0, clinic = 0
        for (const day of d.days || []) { total += Number(day.total) || 0; clinic += Number(day.clinic) || 0 }
        setData({ total, clinic })
      })
      .catch(() => { if (!cancelled) setData('error') })
    return () => { cancelled = true }
  }, [start, end])

  if (data === 'error') return <span className="text-zinc-300" title="โหลดรายได้ไม่สำเร็จ">—</span>
  if (!data) return <span className="inline-flex justify-end text-zinc-300"><Loader2 size={13} className="animate-spin" /></span>

  const { total, clinic } = data
  if (total <= 0) return <span className="text-zinc-300 tabular-nums">—</span>
  return (
    <div className="leading-tight" title={`รวมทั้งหมด ${thb(total)} · ส่วนคลินิก ${thb(clinic)}`}>
      <div className="text-[13px] font-semibold text-emerald-700 tabular-nums">{thb(total, { compact: true })}</div>
      <div className="text-[10.5px] text-zinc-400 tabular-nums">คลินิก {thb(clinic, { compact: true })}</div>
    </div>
  )
}


/* ──────────────────────────────── Calendar view ──────────────────────────────── */

const MAX_LANES_PER_WEEK = 3   // events visible per cell before "+N more"

function OperationsCalendar({ rows, monthAnchor }) {
  const navigate = useNavigate()

  // Build the visible 6-week grid (Sun..Sat × 6).
  const weeks = useMemo(() => {
    const start = gridStart(monthAnchor)
    const out = []
    for (let w = 0; w < 6; w++) {
      const days = []
      for (let i = 0; i < 7; i++) {
        const d = new Date(start)
        d.setDate(start.getDate() + (w * 7 + i))
        days.push(d)
      }
      out.push(days)
    }
    return out
  }, [monthAnchor])

  // For each week, slice every operation into a SEGMENT (the part that falls
  // within this week) and pack the segments into lanes via first-fit so they
  // visually never overlap. Segments carry isStart/isEnd flags so we can draw
  // ribbon-style chips at week boundaries.
  const weekData = useMemo(() => {
    return weeks.map((week) => {
      const wkStart = startOfDay(week[0])
      const wkEnd   = startOfDay(week[6])
      const segments = []
      for (const o of (rows || [])) {
        if (!o.start_date) continue
        const opStart = dateFromKey(o.start_date)
        const opEnd   = dateFromKey(o.end_date || o.start_date)
        // Skip ops that don't intersect this week
        if (opEnd < wkStart || opStart > wkEnd) continue
        const segStart = opStart > wkStart ? opStart : wkStart
        const segEnd   = opEnd   < wkEnd   ? opEnd   : wkEnd
        const startCol = daysBetween(wkStart, segStart)
        const endCol   = daysBetween(wkStart, segEnd)
        segments.push({
          op: o,
          startCol,
          endCol,
          isStart: opStart >= wkStart,   // visual: rounded-l, show time
          isEnd:   opEnd   <= wkEnd,     // visual: rounded-r
          // sort: longer spans (likely "anchors") and earlier starts go first
          sortKey: opStart.getTime() * 1000 - (endCol - startCol),
        })
      }
      segments.sort((a, b) => a.sortKey - b.sortKey)

      // Lane packing (first-fit). Each lane is an array of (startCol, endCol)
      // ranges already placed; a segment fits in lane L if it doesn't overlap.
      const lanes = []
      const placed = []
      for (const s of segments) {
        let lane = 0
        for (; lane < lanes.length; lane++) {
          const used = lanes[lane]
          if (used.every((r) => s.endCol < r.startCol || s.startCol > r.endCol)) break
        }
        if (lane === lanes.length) lanes.push([])
        lanes[lane].push({ startCol: s.startCol, endCol: s.endCol })
        placed.push({ ...s, lane })
      }

      // Per-cell overflow count (events past MAX_LANES_PER_WEEK).
      const overflowByCol = Array(7).fill(0)
      const visible = []
      for (const p of placed) {
        if (p.lane < MAX_LANES_PER_WEEK) {
          visible.push(p)
        } else {
          for (let c = p.startCol; c <= p.endCol; c++) overflowByCol[c]++
        }
      }
      return { week, visible, overflowByCol }
    })
  }, [weeks, rows])

  const today = toDateStr(new Date())
  const monthIdx = monthAnchor.getMonth()

  return (
    <div className={CARD}>
      <div className="grid grid-cols-7 border-b border-zinc-100 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
          <div key={d} className="px-2 py-2 text-center">{d}</div>
        ))}
      </div>

      {weekData.map(({ week, visible, overflowByCol }, wIdx) => {
        const isLastRow = wIdx === 5
        const laneCount = Math.min(MAX_LANES_PER_WEEK, Math.max(0, ...visible.map((v) => v.lane + 1)))
        return (
          <div key={wIdx} className={'grid grid-cols-7 relative ' + (isLastRow ? '' : 'border-b border-zinc-100')}>
            {/* Day cells (date number + overflow indicator + min-height) */}
            {week.map((d, i) => {
              const k = toDateStr(d)
              const inMonth = d.getMonth() === monthIdx
              const isToday = k === today
              const isLastCol = i === 6
              return (
                <div key={i}
                  className={
                    'p-1.5 border-zinc-100 relative ' +
                    (isLastCol ? '' : 'border-r ') +
                    (inMonth ? 'bg-white' : 'bg-zinc-50/60')
                  }
                  style={{ minHeight: 28 + laneCount * 22 + (overflowByCol[i] ? 18 : 0) }}
                >
                  <div className="flex items-center justify-between mb-1 px-1">
                    <span className={
                      'inline-flex items-center justify-center text-[11px] tabular-nums ' +
                      (isToday
                        ? 'w-5 h-5 rounded-full bg-amber-500 text-white font-semibold'
                        : (inMonth ? 'text-zinc-700 font-medium' : 'text-zinc-400'))
                    }>
                      {d.getDate()}
                    </span>
                  </div>
                  {overflowByCol[i] > 0 && (
                    <div className="absolute left-1.5 right-1.5"
                      style={{ top: 28 + MAX_LANES_PER_WEEK * 22 }}>
                      <span className="text-[10px] text-zinc-500 hover:text-zinc-900 cursor-default">
                        +{overflowByCol[i]} more
                      </span>
                    </div>
                  )}
                </div>
              )
            })}

            {/* Event bars (spanning) — positioned absolutely over the cells */}
            {visible.map((seg) => {
              const span = seg.endCol - seg.startCol + 1
              const cls = STAGE_BADGE[seg.op.stage] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'
              return (
                <button
                  key={`${seg.op.id}-${wIdx}`}
                  onClick={() => navigate(`/operations/${seg.op.id}`)}
                  title={`${seg.op.company_name}\n${fmtRange(seg.op.start_date, seg.op.end_date, seg.op.start_time, seg.op.end_time)}\nstage: ${seg.op.stage}`}
                  className={
                    'absolute text-left px-1.5 text-[10.5px] font-medium ring-1 leading-tight ' +
                    'truncate hover:ring-2 hover:brightness-105 transition cursor-pointer ' +
                    cls + ' ' +
                    (seg.isStart ? 'rounded-l ' : '') +
                    (seg.isEnd   ? 'rounded-r ' : '')
                  }
                  style={{
                    top:    28 + seg.lane * 22,
                    height: 20,
                    left:   `calc(${(seg.startCol / 7) * 100}% + 4px)`,
                    width:  `calc(${(span / 7) * 100}% - 8px)`,
                  }}
                >
                  {seg.isStart ? (
                    <>
                      <span className="tabular-nums text-[9.5px] opacity-70 mr-1">{seg.op.start_time || ''}</span>
                      {seg.op.company_name}
                    </>
                  ) : (
                    <span className="opacity-75">↳ {seg.op.company_name}</span>
                  )}
                </button>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

// ── helpers used by the calendar bar packing ──
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
function daysBetween(a, b) {
  // Floor-difference in calendar days (assumes both are startOfDay-ish).
  return Math.round((b - a) / 86400000)
}


/* ──────────────────────────────── Month nav (calendar) ──────────────────────────────── */

function MonthNav({ anchor, onChange }) {
  const label = anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  function shift(delta) {
    const d = new Date(anchor)
    d.setMonth(d.getMonth() + delta)
    onChange(firstOfMonth(d))
  }
  return (
    <div className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-md ring-1 ring-zinc-200 bg-white">
      <button
        type="button"
        onClick={() => shift(-1)}
        className="w-7 h-7 inline-flex items-center justify-center rounded text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
        aria-label="Previous month"
      >
        <ChevronLeft size={14} />
      </button>
      <span className="px-2 text-[12.5px] font-medium text-zinc-900 tabular-nums select-none min-w-[120px] text-center">
        {label}
      </span>
      <button
        type="button"
        onClick={() => shift(+1)}
        className="w-7 h-7 inline-flex items-center justify-center rounded text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
        aria-label="Next month"
      >
        <ChevronRight size={14} />
      </button>
      <button
        type="button"
        onClick={() => onChange(firstOfMonth(new Date()))}
        className="ml-1 px-2 h-7 text-[11.5px] font-medium text-zinc-500 hover:text-amber-700 hover:bg-amber-50 rounded"
      >
        Today
      </button>
    </div>
  )
}


/* ──────────────────────────────── Helpers ──────────────────────────────── */

function StageChip({ stage }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md ring-1 text-[11.5px] font-medium ${STAGE_BADGE[stage] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${STAGE_DOT[stage] || 'bg-zinc-400'}`} />
      {stage}
    </span>
  )
}

function pad(n) { return String(n).padStart(2, '0') }
function toDateStr(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }
function dateFromKey(key) {
  if (!key) return new Date(NaN)
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}
function firstOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1) }
// Start of the visible 6-week grid: the Sunday on/before the 1st of the month.
function gridStart(monthAnchor) {
  const first = firstOfMonth(monthAnchor)
  const day = first.getDay()                          // 0 = Sun
  const d = new Date(first)
  d.setDate(first.getDate() - day)
  return d
}
function gridEnd(monthAnchor) {
  const start = gridStart(monthAnchor)
  const d = new Date(start)
  d.setDate(start.getDate() + 41)                     // 6 weeks × 7 - 1
  return d
}


/* ──────────────────────────────── New-operation modal ──────────────────────
 *  Minimum-viable create form: company + start/end + N + (optional)
 *  coordinator + location override. Stage defaults to 'draft'; staff move it
 *  through the pipeline from the detail page (separate slice).
 */
function NewOperationModal({ onClose, onCreated }) {
  const navigate = useNavigate()
  // Default: 7 days from now, 09:00–17:00 — most ops are full-day visits.
  const defaults = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 7)
    return { date: toDateStr(d), startT: '09:00', endT: '17:00' }
  }, [])

  const [company, setCompany]     = useState(null)
  const [startDate, setStartDate] = useState(defaults.date)
  const [endDate, setEndDate]     = useState(defaults.date)
  const [startTime, setStartTime] = useState(defaults.startT)
  const [endTime, setEndTime]     = useState(defaults.endT)
  const [expected, setExpected]   = useState('')
  const [stage, setStage]         = useState('draft')
  const [saving, setSaving]       = useState(false)
  const [err, setErr]             = useState(null)

  // Keep end_date >= start_date as the user types.
  function onStartDateChange(v) {
    setStartDate(v)
    if (endDate < v) setEndDate(v)
  }

  async function save() {
    setErr(null)
    if (!company?.id)                 { setErr('กรุณาเลือกบริษัท');               return }
    if (!startDate || !endDate)       { setErr('กรุณาระบุวันที่เริ่มและสิ้นสุด'); return }
    if (!startTime || !endTime)       { setErr('กรุณาระบุเวลาเริ่มและสิ้นสุด'); return }
    if (endDate < startDate)          { setErr('วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่ม'); return }
    if (cmpDateTime(endDate, endTime, startDate, startTime) <= 0) {
      setErr('เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม'); return
    }

    setSaving(true)
    try {
      const { id } = await api.operations.create({
        company_id:           company.id,
        start_date:           startDate,
        end_date:             endDate,
        start_time:           startTime,
        end_time:             endTime,
        expected_patients:    expected === '' ? null : parseInt(expected, 10),
        stage,
      })
      onCreated()
      if (id) navigate(`/operations/${id}`)
    } catch (e) {
      const m = (e.message || '').match(/:\s*\{?"?error"?:?\s*"?([^"}]+)/)
      setErr(m ? m[1] : e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-zinc-900/30 backdrop-blur-[1px]" onClick={() => !saving && onClose()} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[560px] max-h-[92vh] flex flex-col">
        <header className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-amber-700/80 font-semibold">New operation</div>
            <h2 className="text-[16px] font-bold text-zinc-900">เพิ่ม Operation ใหม่</h2>
          </div>
          <button onClick={onClose} disabled={saving} className="w-8 h-8 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 inline-flex items-center justify-center disabled:opacity-50">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3.5">
          <Field label="บริษัท" hint="พิมพ์ค้นหาหรือเลือกจากรายการ">
            <CompanyCombobox
              value={company}
              onChange={setCompany}
              placeholder="ค้นหาบริษัท…"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="วันที่เริ่ม">
              <input type="date" value={startDate} onChange={(e) => onStartDateChange(e.target.value)} className={modalInput} />
            </Field>
            <Field label="วันที่สิ้นสุด">
              <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className={modalInput} />
            </Field>
            <Field label="เวลาเริ่ม">
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={modalInput} />
            </Field>
            <Field label="เวลาสิ้นสุด">
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={modalInput} />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="จำนวนคนไข้คาดการณ์">
              <input type="number" min={0} value={expected} onChange={(e) => setExpected(e.target.value)} className={modalInput} placeholder="—" />
            </Field>
            <Field label="Stage">
              <select value={stage} onChange={(e) => setStage(e.target.value)} className={modalInput + ' pr-8'}>
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

        </div>

        <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3">
          {err
            ? <div className="text-[12.5px] text-rose-600 inline-flex items-center gap-1.5"><AlertTriangle size={13} /> {err}</div>
            : <div className="text-[11.5px] text-zinc-400">เปิดหน้า Operation ทันทีหลังบันทึก</div>}
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={saving} className="px-3 py-1.5 rounded-md text-[13px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-medium shadow-sm shadow-amber-500/30 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              บันทึก
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

const modalInput = 'w-full px-3 py-2 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none bg-white text-[13.5px]'


/* ──────────────────────────────── Activity summary ────────────────────────────────
 *  Mirrors the Follow-ups page hero + monthly chart. The key difference: we
 *  count operation-DAYS (each op contributes one count per calendar day it
 *  spans, Bangkok-local), not operation records. So a 3-day op = +3 not +1.
 *  Overlapping ops on the same day stack (utilization view).
 * ──────────────────────────────────────────────────────────────────────────────── */

const TH_MON = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
function labelDay(key)   { const [, m, d] = key.split('-'); return `${Number(d)} ${TH_MON[Number(m) - 1]}` }
function labelMonth(key) { const [y, m] = key.split('-'); return `${TH_MON[Number(m) - 1]} ${String(Number(y) + 543).slice(2)}` }
function nextMonthKey(k) { let [y, m] = k.split('-').map(Number); m += 1; if (m > 12) { m = 1; y += 1 } return `${y}-${String(m).padStart(2, '0')}` }

// Operation-DAYS by calendar date within [from, to]: each op contributes 1 per
// day it spans (Bangkok-local strings, no zone math). Mirrors the old server
// metric. Overlapping ops on a day stack (utilization view).
function opDaysByDay(ops, from, to) {
  const m = new Map()
  for (const o of ops || []) {
    if (!o?.start_date) continue
    const s = o.start_date, e = o.end_date || o.start_date
    const lo = s <= e ? s : e
    const hi = s <= e ? e : s
    let cur = lo < from ? from : lo
    const end = hi > to ? to : hi
    let guard = 0
    while (cur <= end && guard++ < 800) { m.set(cur, (m.get(cur) || 0) + 1); cur = addDays(cur, 1) }
  }
  return m
}
function sumDays(map, from, to) {
  let n = 0
  for (const [d, c] of map) if (d >= from && d <= to) n += c
  return n
}
// Bucket op-days over the current window into a chart series — daily for short
// windows (≤ ~3 months), monthly for longer ones.
function buildOpTrend(byDay, curStart, curEnd) {
  const spanDays = Math.round((Date.parse(curEnd + 'T00:00:00') - Date.parse(curStart + 'T00:00:00')) / 86400000) + 1
  if (spanDays <= 92) {
    const series = []
    let c = curStart, guard = 0
    while (c <= curEnd && guard++ < 400) { series.push({ key: c, count: byDay.get(c) || 0 }); c = addDays(c, 1) }
    return { unit: 'day', series }
  }
  const byMonth = new Map()
  for (const [d, n] of byDay) { if (d < curStart || d > curEnd) continue; const k = d.slice(0, 7); byMonth.set(k, (byMonth.get(k) || 0) + n) }
  const series = []
  let m = curStart.slice(0, 7); const endM = curEnd.slice(0, 7); let guard = 0
  while (m <= endM && guard++ < 120) { series.push({ key: m, count: byMonth.get(m) || 0 }); m = nextMonthKey(m) }
  return { unit: 'month', series }
}

function OperationsStats({ ops, win, period, onPeriodChange }) {
  const loading = ops == null || ops === 'error'
  const { hero, trend } = useMemo(() => {
    if (!Array.isArray(ops)) return { hero: null, trend: null }
    const byDay = opDaysByDay(ops, win.prevStart || win.curStart, win.curEnd)
    const count = sumDays(byDay, win.curStart, win.curEnd)
    const prev  = win.prevStart ? sumDays(byDay, win.prevStart, win.prevEnd) : null
    const delta_pct = (prev == null || prev === 0) ? null : Math.round(((count - prev) / prev) * 100)
    return { hero: { count, prev, delta_pct }, trend: buildOpTrend(byDay, win.curStart, win.curEnd) }
  }, [ops, win])

  return (
    <section className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
      <OperationsHero hero={hero} period={period} loading={loading} />
      <OperationsTrend trend={trend} period={period} onPeriodChange={onPeriodChange} loading={loading} />
    </section>
  )
}

function OperationsHero({ hero, period, loading }) {
  const count = hero?.count ?? 0
  const prev  = hero?.prev
  const delta = hero?.delta_pct
  const label = PERIODS.find((p) => p.key === period)?.label || ''

  let TrendIcon, trendLabel, trendCls
  if (delta == null) {
    TrendIcon = Minus
    trendLabel = prev == null ? 'no comparison' : count === 0 ? 'no prior data' : `+${count} new`
    trendCls = count > 0 ? 'text-emerald-700 bg-emerald-50 ring-emerald-200/70' : 'text-zinc-500 bg-zinc-50 ring-zinc-200/70'
  } else if (delta > 0) {
    TrendIcon = TrendingUp;   trendLabel = `+${delta}%`;  trendCls = 'text-emerald-700 bg-emerald-50 ring-emerald-200/70'
  } else if (delta < 0) {
    TrendIcon = TrendingDown; trendLabel = `${delta}%`;   trendCls = 'text-rose-700 bg-rose-50 ring-rose-200/70'
  } else {
    TrendIcon = Minus;        trendLabel = 'no change';   trendCls = 'text-zinc-600 bg-zinc-50 ring-zinc-200/70'
  }

  return (
    <div className={`${CARD} p-5`}>
      <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-[34px] font-bold tabular-nums text-zinc-900 leading-none">
          {loading ? '–' : count.toLocaleString()}
        </div>
        <div className="text-[12px] text-zinc-500">operation-days</div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ring-1 text-[11.5px] font-medium ${trendCls}`}>
          <TrendIcon size={12} strokeWidth={2.5} />
          {trendLabel}
        </span>
        <span className="text-[11.5px] text-zinc-400 tabular-nums">
          vs ช่วงก่อนหน้า ({loading || prev == null ? '–' : prev.toLocaleString()})
        </span>
      </div>
      <p className="mt-3 text-[11px] text-zinc-400">นับ 1 ต่อทุกวันที่งานครอบคลุม</p>
    </div>
  )
}

function OperationsTrend({ trend, period, onPeriodChange, loading }) {
  const data = trend?.series || []
  const unit = trend?.unit || 'day'
  const total = data.reduce((acc, m) => acc + m.count, 0)
  const [hoverIdx, setHoverIdx] = useState(null)
  const fmtLabel = unit === 'month' ? labelMonth : labelDay

  const W = 540, H = 100
  const padT = 6, padB = 6
  const inner = { x: 0, y: padT, w: W, h: H - padT - padB }
  const max = Math.max(1, ...data.map((m) => m.count))
  const stepX = data.length > 1 ? inner.w / (data.length - 1) : 0
  const pts = data.map((m, i) => {
    const x = i * stepX
    const y = inner.y + inner.h - (m.count / max) * inner.h
    return { x, y, normX: data.length > 1 ? i / (data.length - 1) : 0.5, normY: m.count / max, m }
  })
  const linePath = smoothPath(pts)
  const areaPath = pts.length
    ? `${linePath} L ${pts[pts.length - 1].x.toFixed(1)} ${inner.y + inner.h} L 0 ${inner.y + inner.h} Z`
    : ''

  const labelIdx = data.length <= 1 ? [0] : [0, Math.floor(data.length / 2), data.length - 1]
  // Don't render a dot per point when the series is dense (daily over a long
  // window) — it gets noisy. Dots only for ≤ 31 points.
  const showDots = pts.length <= 31

  const handleMove = (e) => {
    if (!pts.length) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const ratio = Math.max(0, Math.min(1, x / rect.width))
    const idx = Math.round(ratio * (pts.length - 1))
    setHoverIdx(idx)
  }

  return (
    <div className={`${CARD} p-5 md:col-span-2 relative`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">ปริมาณงาน (operation-days)</div>
          <p className="text-[12px] text-zinc-400 mt-0.5">
            {loading ? '–' : <><span className="text-zinc-900 font-semibold text-[13px]">{total.toLocaleString()}</span> รวม · {unit === 'month' ? 'ต่อเดือน' : 'ต่อวัน'}</>}
          </p>
        </div>
        <div className="overflow-x-auto"><PeriodPills value={period} onChange={onPeriodChange} /></div>
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="h-[120px] flex items-center justify-center text-zinc-300 text-[12px]">Loading…</div>
        ) : !data.length ? (
          <div className="h-[120px] flex items-center justify-center text-zinc-300 text-[12px]">ไม่มีข้อมูลในช่วงนี้</div>
        ) : (
          <div className="relative h-[120px] cursor-crosshair"
               onMouseMove={handleMove}
               onMouseLeave={() => setHoverIdx(null)}>
            <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
              <defs>
                <linearGradient id="ttOpFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#f59e0b" stopOpacity="0.30" />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              {[0.25, 0.5, 0.75, 1].map((t) => (
                <line key={t} x1={0} x2={W}
                  y1={inner.y + inner.h - inner.h * t}
                  y2={inner.y + inner.h - inner.h * t}
                  stroke="#f4f4f5" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              ))}
              {pts.length > 0 && <path d={areaPath} fill="url(#ttOpFill)" />}
              {pts.length > 0 && (
                <path d={linePath} fill="none" stroke="#f59e0b" strokeWidth="2"
                  strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              )}
            </svg>

            {hoverIdx != null && pts[hoverIdx] && (
              <div aria-hidden className="absolute top-0 bottom-0 w-px bg-zinc-300/80 pointer-events-none"
                style={{ left: `${pts[hoverIdx].normX * 100}%` }} />
            )}

            {showDots && pts.map((p, i) => {
              const active = i === hoverIdx
              return (
                <div key={i}
                  className={'absolute rounded-full bg-white border-2 border-amber-500 -translate-x-1/2 -translate-y-1/2 transition-transform ' +
                    (active ? 'w-3 h-3 shadow-sm shadow-amber-500/30' : 'w-2 h-2')}
                  style={{ left: `${p.normX * 100}%`, top: `${(p.y / H) * 100}%` }} />
              )
            })}

            {hoverIdx != null && pts[hoverIdx] && (
              <div className="absolute -translate-x-1/2 -translate-y-full -mt-3 pointer-events-none"
                style={{ left: `${pts[hoverIdx].normX * 100}%`, top: `${(pts[hoverIdx].y / H) * 100}%` }}>
                <div className="bg-zinc-900 text-white text-[11px] font-medium px-2 py-1 rounded-md shadow-[0_4px_12px_-4px_rgba(0,0,0,0.4)] whitespace-nowrap tabular-nums">
                  <span className="text-zinc-400 mr-1.5">{fmtLabel(pts[hoverIdx].m.key)}</span>
                  <span>{pts[hoverIdx].m.count.toLocaleString()} op-days</span>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && data.length > 0 && (
          <div className="relative mt-1 h-4 text-[10px] text-zinc-400">
            {labelIdx.map((i) => (
              <span key={i}
                className="absolute -translate-x-1/2 whitespace-nowrap"
                style={{
                  left: `${(i / Math.max(1, data.length - 1)) * 100}%`,
                  transform: i === 0 ? 'translateX(0)' : i === data.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                }}>
                {fmtLabel(data[i]?.key)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
